import { useState } from 'react';

import { deserialisePeople } from '@api/lendingAPI/CustomerStateAPIHelper';
import { useSplitTests } from '@api/lendingAPI/lapiHooks';
import { buildAddressLabel } from '@forms/autoAddress/AutoAddressHelper';
import { FormattedName } from '@iwIntlProvider/FormattedName';
import { tracking } from '@iwoca/frontend-tracking-library';
import {
    IconCheckCircleFilled,
    IconExclamationTriangleFilled,
    IconPenFilled,
    IconQuestionMarkOutline,
    IconTimesFilled,
    IconTrashOutline
} from '@iwoca/icons';
import {
    GetFundingRequirementsCreditFacilityResponse,
    PutStateRequestBody,
    fetchGetStateByStateKey,
    fetchPutState
} from '@iwoca/lapi-client/edge';
import { AlertBox, Button, Card, Pill, Spinner } from '@iwoca/orion';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { MSHForm } from './MSHForm';
import { TPerson } from './MSHForm.types';
import messages from './MSHModal.messages';
import styles from './MSHModal.module.css';
import * as IwDataDogLogger from '../../../../../iwLogger/IwDataDogLogger';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import {
    COMPLETE_APPLICATION_ACTION,
    MAJOR_SHAREHOLDER_ADDED
} from '../../../../drawDown/DrawDown.actions';
import { LoadingAnimation } from '../../../../ui/loadingAnimation/LoadingAnimation';
import SigningDocumentContentPending from '../../signDocumentAction/signDocumentModals/SigningDocumentContentPending';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    fetchFundingRequirements: (
        onUpdatedFundingRequirements: (
            fundingRequirements: GetFundingRequirementsCreditFacilityResponse['data']
        ) => void
    ) =>
        dispatch({
            type: MAJOR_SHAREHOLDER_ADDED,
            onUpdatedFundingRequirements
        }),
    openCallUsModal: () =>
        dispatch({
            type: COMPLETE_APPLICATION_ACTION,
            completedAction: {
                disabled: false,
                status: 'ERROR',
                type: 'APPLICANT_CALL'
            }
        })
});

type PropsFromRedux = ReturnType<typeof mapDispatchToProps>;

const MSHModal = ({
    fetchFundingRequirements,
    onClose,
    openCallUsModal
}: {
    onClose: () => void;
    openCallUsModal: () => void;
} & PropsFromRedux) => {
    const [formVisible, setFormVisible] = useState(false);
    const [showFailedAddPersonMessage, setShowFailedAddPersonMessage] =
        useState(false);
    const [showPersonDidNotResolveFRError, setShowPersonDidNotResolveFRError] =
        useState(false);
    const [showFRSuccessMessage, setShowFRSuccessMessage] = useState(false);
    const [isFetchingFundingRequirements, setIsFetchingFundingRequirements] =
        useState(false);
    const [isDeletingPerson, setIsDeletingPerson] = useState(false);
    const [personBeingEdited, setPersonBeingEdited] = useState<TPerson>();
    const stateKey = useStateKey();

    const { data: customerState, isLoading: customerStateLoading } = useQuery({
        queryKey: ['customer_state', stateKey],
        queryFn: () => fetchGetStateByStateKey({ stateKey })
    });

    const openForm = () => {
        setShowPersonDidNotResolveFRError(false);
        setFormVisible(true);
    };
    const closeForm = () => {
        setFormVisible(false);
        setShowFailedAddPersonMessage(false);
    };

    const queryClient = useQueryClient();
    const saveCustomerState = useMutation({
        mutationFn: (state: PutStateRequestBody) =>
            fetchPutState({
                stateKey: stateKey,
                body: state
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['customer_state', stateKey]
            });
        }
    });

    if (customerStateLoading) return <SigningDocumentContentPending />;

    const people = customerState!.data.application!.people!;

    const deserialisedPeople = deserialisePeople(people!);

    const deletePerson = async (personID: string) => {
        setIsDeletingPerson(true);

        const peopleWithoutDeletedPerson = people?.filter(
            (person) => person.uid !== personID
        );

        const newCustomerState = produce(customerState, (draft) => {
            draft!.data.application!.people = peopleWithoutDeletedPerson;
        });

        try {
            await saveCustomerState.mutateAsync(newCustomerState);
            setIsDeletingPerson(false);
        } catch (error: any) {
            IwDataDogLogger.logError(
                error.message,
                {
                    tags: { reporter: 'msh_modal_delete_person_function' }
                },
                error
            );
        }
    };

    const editPerson = (personID: string) => {
        const personBeingEdited = deserialisedPeople.find(
            (person) => person.uid === personID
        );
        setPersonBeingEdited(personBeingEdited!);
        openForm();
    };

    return (
        <div data-testid="director-shareholder-people-container">
            <div className={styles.peopleHeader}>
                <div className={styles.peopleHeading}>
                    <h3 className={styles.peopleTitle}>
                        <FormattedMessage {...messages.peopleTitle} />
                    </h3>

                    <span
                        className={`${styles.peopleSubtitle} ${
                            formVisible ? styles.peopleSubtitleHide : null
                        }`}
                    >
                        <FormattedMessage {...messages.peopleSubtitle} />
                    </span>
                </div>
                <AlertBox variant="connect" className={styles.fraudAlert}>
                    <IconQuestionMarkOutline height="20" width="20" />
                    <span className={styles.alertMessage}>
                        <FormattedMessage {...messages.fraudAlertMessage} />
                    </span>
                </AlertBox>
                {/* // TODO: Combine error messages with conditional text */}
                {showFailedAddPersonMessage && (
                    <AlertBox variant="failure" className={styles.failed}>
                        <IconExclamationTriangleFilled height="20" width="20" />
                        <span className={styles.alertMessage}>
                            <FormattedMessage {...messages.addPersonError} />
                        </span>
                    </AlertBox>
                )}
                {showPersonDidNotResolveFRError && (
                    <AlertBox variant="failure" className={styles.failed}>
                        <IconExclamationTriangleFilled
                            height="20"
                            width="20"
                            fill="rgba(250, 65, 55, 1)"
                            className={styles.failedIcon}
                        />
                        <span className={styles.alertMessage}>
                            <FormattedMessage
                                {...messages.didNotResolveFundingRequirementError}
                            />{' '}
                            <Button
                                variant="tertiary"
                                className={styles.helpCTA}
                                onClick={() => {
                                    onClose();
                                    openCallUsModal();
                                }}
                            >
                                <FormattedMessage
                                    {...messages.didNotResolveFundingRequirementErrorLink}
                                />
                            </Button>
                        </span>
                    </AlertBox>
                )}
            </div>
            {!formVisible ? (
                <div className={styles.peopleBody}>
                    {isFetchingFundingRequirements && (
                        <div className={styles.overlay}>
                            <LoadingAnimation />
                            <p>
                                <FormattedMessage
                                    {...messages.fundingRequirementsLoading}
                                />
                            </p>
                        </div>
                    )}

                    {isDeletingPerson && (
                        <div
                            className={`${styles.overlay} ${styles.deletingPersonOverlay}`}
                        >
                            <Spinner />
                        </div>
                    )}

                    {showFRSuccessMessage && (
                        <div className={styles.overlay}>
                            <IconCheckCircleFilled
                                className={styles.successIcon}
                            />
                            <p>
                                <FormattedMessage
                                    {...messages.successMessage}
                                />
                            </p>
                            <Button variant="success" onClick={onClose}>
                                <FormattedMessage
                                    {...messages.successMessageCTA}
                                />
                            </Button>
                        </div>
                    )}

                    <div className={styles.peopleContainer}>
                        {deserialisedPeople.map((person) => (
                            <PersonCard
                                person={person}
                                key={person.uid}
                                onDeleteConfirm={(personID) =>
                                    deletePerson(personID)
                                }
                                onEdit={(personID) => {
                                    editPerson(personID);
                                }}
                            />
                        ))}
                    </div>

                    <Button
                        variant="tertiary"
                        onClick={() => {
                            tracking.ampli.drawdownMshFormOpened();
                            openForm();
                        }}
                    >
                        <FormattedMessage {...messages.addPersonCTA} />
                    </Button>
                </div>
            ) : (
                <MSHForm
                    onSubmitSuccess={() => {
                        setIsFetchingFundingRequirements(true);
                        setShowFRSuccessMessage(false);
                        setShowPersonDidNotResolveFRError(false);
                        closeForm();

                        fetchFundingRequirements((funding_requirements) => {
                            const MSHFundingRequirement =
                                funding_requirements?.find(
                                    (
                                        fr: GetFundingRequirementsCreditFacilityResponse['data'][0]
                                    ) =>
                                        fr.recommended_resolution_method ===
                                        'major_shareholders_check'
                                );

                            const hasCompletedMSHCheck =
                                MSHFundingRequirement?.status === 'completed';

                            if (hasCompletedMSHCheck) {
                                setShowFRSuccessMessage(true);
                            } else {
                                setShowPersonDidNotResolveFRError(true);
                            }

                            setIsFetchingFundingRequirements(false);
                        });
                    }}
                    onSubmitError={() => setShowFailedAddPersonMessage(true)}
                    onCancel={() => {
                        closeForm();
                        setPersonBeingEdited(undefined);
                    }}
                    personBeingEdited={personBeingEdited}
                />
            )}
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(MSHModal);

const PersonCard = ({
    person: {
        isGuarantor,
        isApplicant,
        isDirector,
        isShareholder,
        firstName,
        lastName,
        dateOfBirth,
        addresses,
        uid: personID
    },
    onDeleteConfirm,
    onEdit
}: {
    person: TPerson;
    onDeleteConfirm: (arg: string) => void;
    onEdit: (arg: string) => void;
}) => {
    const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
    const noPgSplitTest = useSplitTests('no_pg_test_2025');

    return (
        <Card className={styles.personCard}>
            <div>
                {isApplicant && (
                    <Pill variant="green" className={styles.pill}>
                        Applicant
                    </Pill>
                )}
                {isGuarantor && !noPgSplitTest && (
                    <Pill variant="london" className={styles.pill}>
                        Guarantor
                    </Pill>
                )}
                {isDirector && (
                    <Pill variant="yellow" className={styles.pill}>
                        Director
                    </Pill>
                )}
                {isShareholder ? (
                    <Pill variant="red" className={styles.pill}>
                        Major Shareholder
                    </Pill>
                ) : (
                    <Pill className={`${styles.pill} ${styles.negativePill}`}>
                        Not Major Shareholder
                    </Pill>
                )}
            </div>
            <p>
                <FormattedName firstName={firstName} lastName={lastName} />
            </p>
            <p>
                {dateOfBirth!.day}/{dateOfBirth!.month}/{dateOfBirth!.year}
            </p>
            {addresses?.[0] && <p>{buildAddressLabel(addresses![0])}</p>}
            <div className={styles.deletePersonContainer}>
                {!confirmDeleteVisible ? (
                    <>
                        <Button
                            iconOnly
                            variant="tertiary"
                            className={styles.edit}
                            onClick={() => onEdit(personID)}
                            aria-label="edit"
                        >
                            <IconPenFilled fill="#000000" />
                        </Button>
                        {!isGuarantor && !isApplicant && (
                            <Button
                                iconOnly
                                variant="tertiary"
                                onClick={() => setConfirmDeleteVisible(true)}
                                aria-label="delete"
                            >
                                <IconTrashOutline />
                            </Button>
                        )}
                    </>
                ) : (
                    <>
                        <Button
                            iconOnly
                            variant="tertiary"
                            onClick={() => onDeleteConfirm(personID)}
                            aria-label="deleteConfirm"
                        >
                            <IconCheckCircleFilled
                                className={styles.deleteConfirm}
                            />
                        </Button>

                        <Button
                            iconOnly
                            variant="tertiary"
                            onClick={() => setConfirmDeleteVisible(false)}
                            aria-label="deleteCancel"
                        >
                            <IconTimesFilled className={styles.deleteCancel} />
                        </Button>
                    </>
                )}
            </div>
        </Card>
    );
};
